import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  ReferenceBid,
  chapterReferenceApi,
  getErrorMessage,
} from '../services/api';

export type ReferenceWeight = 'low' | 'medium' | 'high';

export interface ReferenceFileSelection {
  fileIds: number[];
  weight: ReferenceWeight;
}

interface ReferenceBidConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  chapterTitle: string;
  initialSelection: ReferenceFileSelection;
  availableReferences: ReferenceBid[];
  onConfirm: (selection: ReferenceFileSelection) => void;
}

const WEIGHT_OPTIONS: { value: ReferenceWeight; label: string; desc: string }[] = [
  { value: 'high', label: '高', desc: '复制微调' },
  { value: 'medium', label: '中', desc: '参考内容与撰写风格' },
  { value: 'low', label: '低', desc: '仅参考撰写风格' },
];

export const ReferenceBidConfigModal: React.FC<ReferenceBidConfigModalProps> = ({
  isOpen,
  onClose,
  projectId,
  chapterTitle,
  initialSelection,
  availableReferences,
  onConfirm,
}) => {
  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<ReferenceBid[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [weight, setWeight] = useState<ReferenceWeight>('medium');

  useEffect(() => {
    if (isOpen) {
      setSelectedFileIds([...initialSelection.fileIds]);
      setWeight(initialSelection.weight);
      setKeyword('');
      setSearchResults(availableReferences);
      setMessage('');
    }
  }, [isOpen, initialSelection, availableReferences]);

  useEffect(() => {
    if (!keyword.trim()) {
      setSearchResults(availableReferences);
      return;
    }
    const kw = keyword.trim().toLowerCase();
    setSearchResults(
      availableReferences.filter((ref) =>
        ref.file_name.toLowerCase().includes(kw)
      )
    );
  }, [keyword, availableReferences]);

  const handleSearchRemote = useCallback(async () => {
    if (!keyword.trim()) {
      setSearchResults(availableReferences);
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const response = await chapterReferenceApi.list(projectId, keyword.trim());
      setSearchResults(response.data.data || []);
    } catch (error) {
      setMessage(getErrorMessage(error, '搜索失败'));
    } finally {
      setLoading(false);
    }
  }, [keyword, projectId, availableReferences]);

  const addFile = (fileId: number) => {
    if (selectedFileIds.includes(fileId)) return;
    setSelectedFileIds((prev) => [...prev, fileId]);
  };

  const removeFile = (fileId: number) => {
    setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
  };

  const getFileInfo = (fileId: number): ReferenceBid | undefined => {
    return availableReferences.find((r) => r.id === fileId);
  };

  const handleConfirm = () => {
    onConfirm({ fileIds: selectedFileIds, weight });
  };

  if (!isOpen) return null;

  const leftList = (
    <div className="flex-1 overflow-y-auto">
      {searchResults.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          暂无参考技术标文件
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">文件名</th>
              <th className="px-3 py-2 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map((ref) => {
              const alreadyAdded = selectedFileIds.includes(ref.id);
              return (
                <tr key={ref.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-700">{ref.file_name}</div>
                    <div className="text-xs text-slate-400">
                      {ref.project_name} &middot; {ref.page_count ?? '-'}页
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {alreadyAdded ? (
                      <span className="text-xs text-emerald-500">&checkmark; 已添加</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => addFile(ref.id)}
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        添加
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  const rightList = (
    <div className="flex-1 overflow-y-auto px-4">
      {selectedFileIds.length === 0 ? (
        <div className="flex h-full items-center justify-center text-center text-sm text-slate-400">
          未参考任何技术标内容，请搜索并添加
        </div>
      ) : (
        <ul className="divide-y divide-slate-50">
          {selectedFileIds.map((fileId) => {
            const info = getFileInfo(fileId);
            return (
              <li key={fileId} className="flex items-center gap-2 py-2">
                <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-slate-300" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-slate-700">
                    {info?.file_name || `#${fileId}`}
                  </div>
                  <div className="truncate text-xs text-slate-400">
                    {info?.project_name || ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(fileId)}
                  className="flex-shrink-0 text-xs text-red-500 hover:text-red-700"
                >
                  移除
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[560px] w-[760px] max-w-full flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800">参考技术标</h3>
            <p className="text-xs text-slate-500">章节：{chapterTitle || '(未选中)'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex w-1/2 flex-col border-r border-slate-100">
            <div className="border-b border-slate-100 px-4 py-3">
              <div className="mb-2 flex gap-2">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索参考技术标文件名"
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearchRemote();
                  }}
                />
                <button
                  type="button"
                  onClick={handleSearchRemote}
                  disabled={loading}
                  className="rounded-lg bg-blue-500 px-3 py-1.5 text-sm text-white hover:bg-blue-600 disabled:opacity-50"
                >
                  <MagnifyingGlassIcon className="inline h-4 w-4" /> 搜索
                </button>
              </div>
              <p className="text-xs text-slate-400">选择范围：当前账号全部项目</p>
            </div>

            {message && (
              <div className="border-b border-slate-100 px-4 py-2 text-xs text-red-500">
                {message}
              </div>
            )}

            {leftList}
          </div>

          <div className="flex w-1/2 flex-col">
            <div className="border-b border-slate-100 px-4 py-3">
              <h4 className="mb-2 text-xs font-medium text-slate-600">
                已参考内容 ({selectedFileIds.length})
              </h4>
            </div>

            {rightList}

            <div className="border-t border-slate-100 px-4 py-3">
              <p className="mb-2 text-xs text-slate-500">
                参考程度 高、中、参考可能修改三、四级目录，低参考则不会修改目录
              </p>
              <div className="flex gap-3">
                {WEIGHT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex flex-1 cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
                    style={{
                      borderColor: weight === opt.value ? '#3b82f6' : '#e2e8f0',
                      backgroundColor: weight === opt.value ? '#eff6ff' : 'white',
                    }}
                  >
                    <input
                      type="radio"
                      name="reference-weight"
                      value={opt.value}
                      checked={weight === opt.value}
                      onChange={() => setWeight(opt.value)}
                      className="accent-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-700">{opt.label}</span>
                      <span className="ml-1 text-xs text-slate-400">({opt.desc})</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-blue-500 px-4 py-1.5 text-sm text-white hover:bg-blue-600"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
};
